import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BaseLayout, SkeletonLoader } from '@/components';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui';
import ROLES from '@/constants/roles.constants';
import { readUser } from '../../../../api/user.api';
import type { User } from '../../../../types/user.types';
import { useToast } from '../../../../contexts/toast-context';
import { TOAST_TYPES } from '../../../../constants/common.constants';
import { UserProfile } from '../../../../components/user-profile';
import { AuthError } from '../../../../utils/errors';
import { useAuth } from '../../../../hooks/use-auth';

import './view-user-style.scss';

export const Route = createFileRoute('/organizations/$uuid/profile/')({
  component: ViewUserProfile,
});

function ViewUserProfile() {
  const { uuid } = Route.useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isAuthenticated, user: currentUser } = useAuth();

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const data = await readUser(uuid);
        setUser(data.user);
      } catch (error) {
        addToast(`Failed to fetch user profile`, {
          type: TOAST_TYPES.ERROR,
          title: 'Fetch Error',
        });
        if (!(error instanceof AuthError) && isAuthenticated) {
          navigate({ to: '/organizations' });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [uuid]);

  if (isLoading) {
    return (
      <BaseLayout
        header={{
          headerText: 'User Profile',
        }}
        breadcrumbs={{
          showBreadcrumbs: true,
          breadcrumbText: ['Users', 'Profile'],
        }}
      >
        <Card>
          <CardContent>
            <SkeletonLoader count={10} height={30} />
          </CardContent>
        </Card>
      </BaseLayout>
    );
  }

  if (!user) {
    return null;
  }

  // Permission checks for edit button
  const canEdit = () => {
    if (!currentUser || !user) return false;

    // Admins can edit all users
    if (currentUser.role === ROLES.ADMINISTRATOR) return true;

    // Executives can edit users in their organization
    if (currentUser.role === ROLES.EXECUTIVE) {
      return currentUser.account === user.accountName;
    }

    // Managers can only edit drivers that report directly to them
    if (currentUser.role === ROLES.MANAGER) {
      return (
        (user.role === ROLES.DRIVER && user.managerUuid === currentUser.uuid) ||
        (user.role === ROLES.MANAGER && user.uuid === currentUser.uuid)
      );
    }

    return false;
  };

  return (
    <BaseLayout
      header={{
        headerText: 'User Profile',
      }}
      breadcrumbs={{
        showBreadcrumbs: true,
        breadcrumbText: [user.name],
      }}
    >
      <Card>
        <CardHeader>
          <div className="flex w-full">
            <div className="flex-1">
              <CardTitle>{user.name}</CardTitle>
            </div>
            <div className="button-container-right">
              <Button
                type="button"
                variant="outline-secondary"
                size="small"
                onClick={() => navigate({ to: `/organizations/${uuid}` })}
              >
                Cancel
              </Button>
              {canEdit() && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="small"
                  onClick={() =>
                    navigate({ to: `/organizations/${uuid}/edit` })
                  }
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <UserProfile user={user} />
      </Card>
    </BaseLayout>
  );
}
